import { Editor } from 'tinymce';
import {
  StepButtons,
  CancelItemsSelection,
  FunnelType,
  StepDurationUnits,
  StepEmulatorDevices,
  StepMedia,
  StepMediaCategory,
  StepResponseScreen,
  StepTextual,
  StepButtonShapes,
  StepButtonShapeStyles,
  StepFixedMediaCategory,
  StepButtonControlTypes,
  defaultThemeColor,
  whiteColor,
  StepMediaSubCategory,
  EmbedPlatforms,
  embedURLs,
  videoFilePattern,
  StepProductsPreview,
  OfferIntents,
  CustomStepCategoryEnum,
  FunnelInputTypeEnum,
  ActionType,
  Image,
  StepResponseMedia,
  CampaignProduct,
  parseFunnelVariableForEmulator,
  DiscountSetting,
  StepElements,
  FunnelStep,
  StepCategory,
} from '../../_models';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OfferBuilderSection, OfferBuilderSectionIntents, RESOLVE_ROUTE_PATH, steps } from './steps-list';
import { ElementRef } from '@angular/core';

const conversions: { [key in StepDurationUnits]: number } = {
  [StepDurationUnits.Hours]: 60 * 60,
  [StepDurationUnits.Days]: 24 * 60 * 60,
  [StepDurationUnits.Weeks]: 7 * 24 * 60 * 60,
  [StepDurationUnits.Months]: 30 * 24 * 60 * 60,
};

export function convertToSeconds(duration: number, unit: StepDurationUnits): number | null {
  if (duration === null || duration === undefined) return null;
  return unit in conversions ? duration * conversions[unit] : duration;
}

export function convertFromSeconds(seconds: number, unit: StepDurationUnits): number | null {
  if (seconds === null || seconds === undefined) return null;
  return conversions[unit] ? seconds / conversions[unit] : seconds;
}

export function capitalizeFirstLetter(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function getOfferBuilderSection(intent: OfferIntents): OfferBuilderSection {
  const sections = Object.keys(OfferBuilderSectionIntents) as OfferBuilderSection[];
  const matchedSection = sections.find(
    (section) => OfferBuilderSectionIntents[section].indexOf(intent) !== -1
  );

  let section = OfferBuilderSection.Retention;
  if (matchedSection) {
    section = matchedSection;
  }
  return section;
}

export function getOfferBuilderRoot(section: OfferBuilderSection): string {
  // the Resolve section lives at /resolve-paths; every other section's route equals its slug
  if (section === OfferBuilderSection.Resolve) {
    return RESOLVE_ROUTE_PATH;
  }

  return section || OfferBuilderSection.Retention;
}

export function getStepDescription(category: StepCategory): string {
  const stepDescriptions: Partial<Record<StepCategory, string>> = {
    [CustomStepCategoryEnum.Survey]:
      'A flexible survey that can be placed anywhere in the system to ask targeted questions.',
    [CustomStepCategoryEnum.CancelOrderSurvey]:
      'Trigger this when customers cancel one-time purchases or orders. Great for identifying buyer friction points.',
    [CustomStepCategoryEnum.CancelTrialSurvey]:
      'Use this to capture feedback when users end a free trial. Can be key to improving conversion.',
    [CustomStepCategoryEnum.CancelSubSurvey]:
      'Show this survey when users attempt to cancel an active subscription. Helps understand and prevent churn.',
    [CustomStepCategoryEnum.ReturnSurvey]:
      'Ideal for shipped orders. Helps collect reasons behind product returns to reduce future issues.',
    [CustomStepCategoryEnum.ResolveSurvey]:
      'Show this when a disputing customer requests a refund. Collects their reason before issuing it.',
  };

  // Try hardcoded description first
  if (stepDescriptions[category]) {
    return stepDescriptions[category]!;
  }

  // Try to find from steps array
  const fallback = steps.find((step) => step.type === category);
  return (fallback && fallback.description) || '';
}

export function addDummyValues(
  content: string,
  variables: { value: string; text: string }[],
  product: CampaignProduct = null,
  discount: DiscountSetting = null
): string {
  let updatedContent = content;
  if (updatedContent) {
    variables.forEach((variable) => {
      if (content.indexOf(variable.value) !== -1) {
        const regex = new RegExp(escapeRegExp(variable.value), 'g');
        const value = parseFunnelVariableForEmulator(variable.value, product, discount);

        updatedContent = updatedContent.replace(regex, value);
      }
    });
  }
  return updatedContent;
}

export function normalizeObject(obj) {
  const fieldsToNull = ['next_step', 'step', 'name'];
  const fieldsToFalse = ['is_template'];
  const fieldsToDelete = ['id'];

  fieldsToFalse.forEach((field) => {
    obj[field] = false;
  });
  fieldsToNull.forEach((field) => {
    obj[field] = null;
  });
  fieldsToDelete.forEach((field) => {
    delete obj[field];
  });

  const recursiveFields = ['inputs', 'actions'];

  recursiveFields.forEach((field) => {
    if (field in obj) {
      obj[field] = obj[field].map((item) => normalizeObject(item));
    }
  });

  return obj;
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function pxToVw(pixels: string, width: number): string {
  const sizeInPixels = parseInt(pixels.replace(/\D/g, ''), 10);
  const sizeInVw = ((sizeInPixels / width) * 100).toFixed(2);
  return sizeInVw;
}

export function vwToPx(vw: string, width: number): number {
  const sizeInPixels = (+vw * width) / 100;
  return sizeInPixels;
}

export function parseStyleString(style: string): any {
  const styleObject = {};
  if (style) {
    style.split(';').forEach((styleRule) => {
      const [property, value] = styleRule.split(':').map((item) => item.trim());
      if (property && value) {
        styleObject[property] = value;
      }
    });
  }
  return styleObject;
}

export function getEmbeddedVideoUrl(videoUrl: string, sanitizer: DomSanitizer): SafeResourceUrl | null {
  let embedUrl: string | null = null;

  if (videoUrl) {
    // YouTube Shorts
    if (videoUrl.includes('youtube.com/shorts/') || videoUrl.includes('youtu.be/shorts/')) {
      const videoId = extractVideoId(videoUrl, EmbedPlatforms.Youtube);
      embedUrl = videoId ? `${embedURLs.youtube}/${videoId}` : null;
    }
    // Regular YouTube
    else if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = extractVideoId(videoUrl, EmbedPlatforms.Youtube);
      embedUrl = videoId ? `${embedURLs.youtube}/${videoId}` : null;
    }
    // Vimeo, DailyMotion cases
    else if (videoUrl.includes('vimeo.com')) {
      const videoId = extractVideoId(videoUrl, EmbedPlatforms.Vimeo);
      embedUrl = videoId ? `${embedURLs.vimeo}/${videoId}` : null;
    } else if (videoUrl.includes('dailymotion.com')) {
      const videoId = extractVideoId(videoUrl, EmbedPlatforms.dailyMotion);
      embedUrl = videoId ? `${embedURLs.dailyMotion}/${videoId}` : null;
    }
    else if (isDirectVideoFile(videoUrl)) {
      embedUrl = videoUrl.trim();
    }

    if (!embedUrl) {
      return null;
    }

    return sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }
}

export function isDirectVideoFile(videoUrl: string): boolean {
  if (!videoUrl) {
    return false;
  }
  const trimmed = videoUrl.trim();
  return /^https?:\/\//i.test(trimmed) && videoFilePattern.test(trimmed);
}

export function extractVideoId(url: string, platform: EmbedPlatforms): string | null {
  let videoId: string | null = null;

  switch (platform) {
    case EmbedPlatforms.Youtube:
      const shortsRegex = /(?:youtube\.com\/shorts\/|youtu\.be\/shorts\/)([a-zA-Z0-9_-]{11})/;
      const youtubeRegex =
        /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

      const shortsMatch = url.match(shortsRegex);
      const youtubeMatch = url.match(youtubeRegex);

      if (shortsMatch && shortsMatch[1]) {
        videoId = shortsMatch[1];
      } else if (youtubeMatch && youtubeMatch[1]) {
        videoId = youtubeMatch[1];
      }
      break;

    case EmbedPlatforms.Vimeo:
      const vimeoRegex = /vimeo\.com\/(?:video\/)?(\d+)/;
      const vimeoMatch = url.match(vimeoRegex);
      if (vimeoMatch && vimeoMatch[1]) {
        videoId = vimeoMatch[1];
      }
      break;

    case EmbedPlatforms.dailyMotion:
      const dailymotionRegex = /dailymotion\.com\/video\/([a-zA-Z0-9]+)/;
      const dailymotionMatch = url.match(dailymotionRegex);
      if (dailymotionMatch && dailymotionMatch[1]) {
        videoId = dailymotionMatch[1];
      }
      break;

    default:
      videoId = null;
      break;
  }

  return videoId;
}

export function getTruncatedProductName(productName: string): string {
  const maxLength = 22;
  if (!productName) {
    return '';
  }

  const trimmedName = productName.trim();
  return trimmedName.length > maxLength ? trimmedName.slice(0, maxLength - 3) + '...' : trimmedName;
}

export const StepBody = {
  subtitle: null,
  style: null,
  classes: [],
  is_popup: false,
  show_step_link: false,
  contact_type: null,
  enhanced_content: null,
  enhanced_mode: false,
  show_selected_product: false,
  fail_if_any_input_invalid: false,
  invalid_message: null,
  hide_status: false,
  action_holdoffs: null,
  mobile_icon_button_type: null,
  use_bootstrap_spacing: true,
  enhanced_content_components: null,
  help: null,
};

export const OfferData = {
  first_step: null,
  is_visual: true,
  is_voice: false,
  is_sms: false,
  is_modified: true,
  is_public: false,
  is_template: false,
  steps: [],
  use_product_filter: false,
  item_statuses: null,
  is_3ds: false,
  cancel_items_selection: CancelItemsSelection.UpsellsOnlyIfUpsell,
  min_billing_cycle: 1,
  merchant_ids: null,
  fulfillment_type: null,
  states: null,
  use_crm_campaign_filter: false,
  crm_campaigns: [],
  cc_types: null,
  payment_types: null,
  countries: null,
  return_items_selection: CancelItemsSelection.ShowAll,
  resourcetype: FunnelType.Product,
};

export function createStepTextualInitialData(): StepTextual {
  return {
    banner: '',
    body: {
      label: '',
      content: '',
    },
  };
}

export function createStepMediaInitialData(): StepMedia {
  return {
    category: StepMediaCategory.NoMedia,
    subCategory: StepMediaSubCategory.Product,
    fixedImage: {
      [StepEmulatorDevices.Mobile]: {} as Image,
      [StepEmulatorDevices.Tablet]: {} as Image,
      [StepEmulatorDevices.Desktop]: {} as Image,
      productImage: null,
      category: StepFixedMediaCategory.Upload,
    },
    embedVideo: { url: null, isURLValid: false, isFile: false, sanitizedURL: null },
  };
}

export function createStepResponseMediaInitialData(): StepResponseMedia {
  return {
    category: StepMediaCategory.NoMedia,
    fixedImage: {
      [StepEmulatorDevices.Mobile]: {} as Image,
      [StepEmulatorDevices.Tablet]: {} as Image,
      [StepEmulatorDevices.Desktop]: {} as Image,
      productImage: null,
      category: StepFixedMediaCategory.Upload,
    },
    embedVideo: { url: null, isURLValid: false, isFile: false, sanitizedURL: null },
  };
}

export function createStepButtonsInitialData(): StepButtons {
  return {
    confirmButton: {
      content: '',
      ...StepButtonShapeStyles[StepButtonShapes.Modern],
      brandBackground: defaultThemeColor,
      brandBorder: whiteColor,
      brandFont: whiteColor,
      fontColor: whiteColor,
      isFontDisabled: false,
      style: StepButtonShapes.Modern,
      type: StepButtonControlTypes.Theme,
      controlPopup: false,
      shapesPopup: false,
    },
    rejectButton: {
      content: '',
      ...StepButtonShapeStyles[StepButtonShapes.ModernOutline],
      brandBackground: whiteColor,
      brandBorder: defaultThemeColor,
      brandFont: defaultThemeColor,
      fontColor: defaultThemeColor,
      isFontDisabled: true,
      style: StepButtonShapes.ModernOutline,
      type: StepButtonControlTypes.Theme,
      controlPopup: false,
      shapesPopup: false,
    },
  };
}

export function createStepResponseScreenInitialData(): StepResponseScreen {
  return {
    detailButton: {
      borderColor: whiteColor,
      backgroundColor: defaultThemeColor,
      fontColor: whiteColor,
    },
    continueButton: {
      borderColor: defaultThemeColor,
      backgroundColor: whiteColor,
      fontColor: defaultThemeColor,
    },
    titleContent: '',
    responseContent: '',
  };
}

export function createStepProductPreviewInitialData(): StepProductsPreview {
  return {
    data: [],
    loading: false,
  };
}

export const FontSizes = '12px 14px 16px 18px 20px 22px 24px 26px 28px 30px 32px 34px 36px';

export const FontFamilyFormats =
  "Open Sans='Open Sans', Ubuntu, sans-serif; Ubuntu=ubuntu,sans-serif; Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times,serif; Courier New=courier new,courier,monospace; Comic Sans MS=comic sans ms,sans-serif; Georgia=georgia,serif; Trebuchet MS=trebuchet ms,geneva;";

export function initializeEditorStyles(editor: Editor, fontSize: string, fontFamily: string, alignCenter?: boolean) {
  const applyBaseStyle = () => {
    const style = document.createElement('style');
    style.innerHTML = `
      body {
        font-size: ${fontSize};
        font-family: ${fontFamily};
      }
    `;
    editor.getDoc().head.appendChild(style);
  };

  const applyDefaultStyles = () => {
    editor.execCommand('FontSize', false, fontSize);
    editor.execCommand('FontName', false, fontFamily);
  };

  const cleanUpContent = () => {
    const content = editor.getContent();
    if (content.includes('<div>&nbsp;</div>')) {
      editor.setContent(content.replace(/<div>&nbsp;<\/div>/g, ''));
    }
  };

  const applyStylesAndCleanUp = () => {
    const content = editor.getContent();
    if (!content || content.trim() === '' || content === '<p><br></p>') {
      applyDefaultStyles();
      cleanUpContent();

      if (alignCenter) {
        editor.formatter.apply('aligncenter');
      }
    }
  };

  editor.on('init', () => {
    // Always give the iframe body a default font-family so the toolbar's
    // font dropdown reports "Open Sans" instead of falling back to "System Font"
    applyBaseStyle();
    applyStylesAndCleanUp();
  });
  editor.on('Change KeyUp', applyStylesAndCleanUp);
  editor.on('focus', cleanUpContent);
}

export function enableImagePaste(editor: Editor) {
  editor.on('paste', (event: ClipboardEvent) => {
    let images: File[] = [];
    if (event.clipboardData && event.clipboardData.items && !event.clipboardData.getData('text/plain')) {
      images = Array.from(event.clipboardData.items)
        .filter((item) => item.kind === 'file' && /^image\/(jpeg|png|gif|bmp|webp)$/.test(item.type))
        .map((item) => item.getAsFile());
    }

    if (images.length) {
      event.preventDefault();
      images.forEach((image) => {
        const reader = new FileReader();
        reader.onload = () => {
          editor.insertContent(`<img src="${reader.result}">`);
          editor.uploadImages(null).then((results: { element: HTMLImageElement; status: boolean }[]) => {
            results.filter((result) => !result.status).forEach((result) => editor.dom.remove(result.element));
            editor.fire('Change');
          });
        };
        reader.readAsDataURL(image);
      });
    }
  });
}

export function addDataAttributes(html: string): string {
  if (!html) return null;
  const fontSizeRegex = /font-size:\s*(\d+px);/g;

  const dataFontSizeRegex = /data-fontsize="[^"]*"/;

  let updatedHtml = html.replace(
    /(<[^>]+)(style="[^"]*font-size:\s*\d+px[^"]*")([^>]*)>/g,
    (match, startTag, styleAttr, endTag) => {
      const fontSizeMatch = styleAttr.match(fontSizeRegex);
      const fontSize = fontSizeMatch ? fontSizeMatch[0].split(':')[1].trim() : null;

      if (fontSize) {
        if (dataFontSizeRegex.test(match)) {
          return match.replace(dataFontSizeRegex, `data-fontsize="${fontSize}"`);
        } else {
          return `${startTag} data-fontsize="${fontSize}" ${styleAttr}${endTag}>`;
        }
      }

      return match;
    }
  );

  return updatedHtml;
}

const MIN_WIDTH = 320;
const MAX_WIDTH = 8000;
const MIN_MULTIPLIER = 1.1;
const MAX_MULTIPLIER = 1.6;
const BASE_WIDTH = 430;

let emulatorWidth: number = BASE_WIDTH;

export function setEmulatorWidth(width: number) {
  emulatorWidth = width;
}

function getDeviceWidth() {
  return emulatorWidth;
}

function calculateScaleMultiplier(deviceWidth: number) {
  deviceWidth = Math.max(MIN_WIDTH, Math.min(deviceWidth, MAX_WIDTH));

  return MIN_MULTIPLIER + ((deviceWidth - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH)) * (MAX_MULTIPLIER - MIN_MULTIPLIER);
}

function calculateFontSize(baseFontSize: string | number) {
  const deviceWidth = getDeviceWidth();
  if (baseFontSize) {
    baseFontSize = parseFloat(baseFontSize.toString());
    const scaleMultiplier = calculateScaleMultiplier(deviceWidth); // Maximum font size multiplier
    let scaledFontSize = (baseFontSize / BASE_WIDTH) * deviceWidth;

    if (deviceWidth < BASE_WIDTH) {
      scaledFontSize = Math.floor(scaledFontSize - 1.5);
    }

    const maxFontSize = baseFontSize * scaleMultiplier; // Dynamic max font size
    return Math.min(scaledFontSize, maxFontSize); // Cap at maxFontSize
  }

  return baseFontSize;
}

export function updateFontSizes(html: string): string {
  let updatedHtml = html || '';

  if (updatedHtml) {
    const fontSizeRegex = /font-size:\s*((\d+(\.\d+)?)px);/;

    let match;
    while ((match = fontSizeRegex.exec(html)) !== null) {
      const originalFontSize = match[1];
      const newFontSize = calculateFontSize(originalFontSize);
      updatedHtml = updatedHtml.replace(new RegExp(escapeRegExp(match[0]), 'g'), `font-size: ${newFontSize}px;`);
      html = html.slice(match.index + 1);
    }
  }

  return updatedHtml;
}

export function scrollToAccordionGroup(
  groupType: string | number,
  groupList: { type: string | number }[],
  elementRefs: ElementRef[]
): void {
  setTimeout(() => {
    const index = groupList.findIndex((g) => g.type === groupType);
    const el = elementRefs[index];

    if (el) {
      const elementY = el.nativeElement.getBoundingClientRect().top + window.scrollY;
      const fixedOffset = 80;

      window.scrollTo({
        top: elementY - fixedOffset,
        behavior: 'smooth',
      });
    }
  }, 50);
}

export const StepCategoryOfferIntentsMapping = {
  [CustomStepCategoryEnum.AdjustShippingFrequency]: OfferIntents.AdjustShippingFrequency,
  [CustomStepCategoryEnum.PauseSurvey]: OfferIntents.PauseSubscription,
  [CustomStepCategoryEnum.ReviseOrder]: OfferIntents.ChangeSubscriptionProduct,
  [CustomStepCategoryEnum.ChangeVariant]: OfferIntents.ChangeVariant,
  [CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount]: OfferIntents.ReactivateSubscription,
  [CustomStepCategoryEnum.OfferDetails]: OfferIntents.OfferDetails,
  [CustomStepCategoryEnum.CancelOrderSurvey]: OfferIntents.CancelOrder,
  [CustomStepCategoryEnum.CancelTrialSurvey]: OfferIntents.CancelTrial,
  [CustomStepCategoryEnum.CancelSubSurvey]: OfferIntents.CancelSubscription,
  [CustomStepCategoryEnum.ReturnSurvey]: OfferIntents.ReturnOrder,
  [CustomStepCategoryEnum.ExtendTrial]: OfferIntents.ExtendTrial,
  [CustomStepCategoryEnum.AdjustTrialFrequency]: OfferIntents.AdjustTrialFrequency,
  [CustomStepCategoryEnum.BillTrialNow]: OfferIntents.BillTrialNow,
};

// Default response Title / Message markup, matching the single-step path response styles (24px title, 22px body)
function buildResponseTitle(text: string): string {
  return (
    `<div class="response-content"><div style="text-align: center;">` +
    `<span style="font-size: 24px; font-family: 'Open Sans', Ubuntu, sans-serif;"><strong>${text}</strong></span></div></div>`
  );
}

function buildResponseMessage(text: string): string {
  return (
    `<div style="text-align: center;">` +
    `<span data-fontsize="22px;" style="font-size: 22px; font-family: 'Open Sans', Ubuntu, sans-serif;">${text}</span></div>`
  );
}

const discountResponseDefault = {
  label: buildResponseTitle('Discount Applied'),
  content: buildResponseMessage('We have applied a <strong>{discount_percent}%</strong> discount to your subscription.'),
};

const refundResponseDefault = {
  label: buildResponseTitle('Refund Processed'),
  content: buildResponseMessage(
    `Your <strong>{refund|currency}</strong> refund has been processed.<br />` +
      `Please allow 7-10 business days for the refund to reflect in your account.`
  ),
};

const reactivateResponseDefault = {
  label: buildResponseTitle('Welcome Back!'),
  content: buildResponseMessage(
    `Your subscription has been successfully reactivated, now enjoy <strong>{discount_percent}% off</strong> ` +
      `as a valued customer. Your next bill date is <strong>{next_bill_date}</strong>`
  ),
};

const orderUpdatedResponseDefault = {
  label: buildResponseTitle('Order Updated'),
  content: buildResponseMessage('Your order has been updated.'),
};

const pauseResponseDefault = {
  label: buildResponseTitle('Subscription Paused'),
  content: buildResponseMessage('Your subscription has been paused.'),
};

const extendTrialResponseDefault = {
  label: buildResponseTitle('Trial Extended'),
  content: buildResponseMessage(
    `Your trial has been extended. We've pushed back your first charge so you have more time to decide.`
  ),
};

const returnDownsellResponseDefault = {
  label: buildResponseTitle('Refund Applied'),
  content: buildResponseMessage(
    `We've applied a <strong>{refund|currency}</strong> refund to your original payment method, ` +
      `and there's no need to return your order.`
  ),
};

// Pre-filled response Title / Message for new downsell steps so each category opens with sensible copy (OP-1973)
export const StepResponseContentDefaults: { [category: number]: { label: string; content: string } } = {
  [CustomStepCategoryEnum.DiscountFutureOrder]: discountResponseDefault,
  [CustomStepCategoryEnum.DiscountWithAdjustShippingFrequency]: discountResponseDefault,
  [CustomStepCategoryEnum.RefundOrder]: refundResponseDefault,
  [CustomStepCategoryEnum.RefundWithDiscount]: refundResponseDefault,
  [CustomStepCategoryEnum.ReActivateSubscription]: reactivateResponseDefault,
  [CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount]: reactivateResponseDefault,
  [CustomStepCategoryEnum.ReviseOrder]: orderUpdatedResponseDefault,
  [CustomStepCategoryEnum.ChangeVariant]: orderUpdatedResponseDefault,
  [CustomStepCategoryEnum.PauseSub]: pauseResponseDefault,
  [CustomStepCategoryEnum.PauseSurvey]: pauseResponseDefault,
  [CustomStepCategoryEnum.ExtendTrial]: extendTrialResponseDefault,
  [CustomStepCategoryEnum.ReturnOrderRMA]: returnDownsellResponseDefault,
  [CustomStepCategoryEnum.AdjustShippingFrequency]: {
    label: buildResponseTitle('Success!'),
    content: buildResponseMessage(
      `We have adjusted the shipping date on your subscription.<br />` +
        `Your next shipment won't be shipped until <strong>{next_bill_date}</strong>`
    ),
  },
  [CustomStepCategoryEnum.SkipSubscription]: {
    label: buildResponseTitle('Shipment Skipped'),
    content: buildResponseMessage('Your next delivery is now scheduled for <strong>{next_bill_date}</strong>.'),
  },
};

export const StepsInvalidityFields = [
  'nameInvalidity',
  'labelInvalidity',
  'contentInvalidity',
  'discountInvalidity',
  'mediaInvalidity',
  'inputsInvalidity',
  'rejectInvalidity',
  'holdOffInvalidity',
  'timeDelayInvalidity',
  'confirmInvalidity',
  'settingsInvalidity',
];

export const InvalidityToStepElementMapping = {
  nameInvalidity: StepElements.Name,
  labelInvalidity: StepElements.Body,
  contentInvalidity: StepElements.Body,
  discountInvalidity: StepElements.Discount,
  mediaInvalidity: StepElements.Media,
  inputsInvalidity: StepElements.Buttons,
  rejectInvalidity: StepElements.Buttons,
  holdOffInvalidity: StepElements.Actions,
  timeDelayInvalidity: StepElements.TimeDelay,
  confirmInvalidity: StepElements.Buttons,
  settingsInvalidity: StepElements.DiscountSettings,
};

export const StepHints = {
  title: 'This is the main question or message customers will see.',
  intro: 'A short message to guide customers.',
  stepName: 'Give your step a name to keep things organized.',
  surveyName: 'Give your survey a name to keep things organized.',
  templateName: 'Give your template a name to keep things organized.',
  responseOption: 'Add choices for customers to select.',
  customButtonLabels: 'Change the text on the buttons to make navigation clearer.',
  surveyNextButton: '"Continue” or “Next"',
  surveyBackButton: '"Go Back” or “Previous"',
};

export function isSurveyStep(step: FunnelStep) {
  const surveyCategories: StepCategory[] = [
    CustomStepCategoryEnum.Survey,
    CustomStepCategoryEnum.CancelOrderSurvey,
    CustomStepCategoryEnum.CancelTrialSurvey,
    CustomStepCategoryEnum.CancelSubSurvey,
    CustomStepCategoryEnum.ReturnSurvey,
    CustomStepCategoryEnum.ResolveSurvey,
    CustomStepCategoryEnum.PauseSurvey,
  ];

  return surveyCategories.indexOf(step.category) > -1;
}

export const defaultConfirmButtonStyles = {
  control_type: 0,
  style:
    'color: #ffffff; background-color: var(--theme_color); border-color: var(--theme_color); border-radius: 5px; min-height: 72px; padding: 16px 10px;',
  style_type: 1,
};

export const defaultRejectButtonStyles = {
  control_type: 1,
  style: 'color: #707070; background-color: #00000000; border-color: #00000000;',
  style_type: 6,
};

export const mediaExcludedSteps: StepCategory[] = [
  CustomStepCategoryEnum.ReviseOrder,
  CustomStepCategoryEnum.ChangeVariant,
  FunnelInputTypeEnum.EnterTroubleshooter,
];

export const descriptionExcludedStepTypes: CustomStepCategoryEnum[] = [
  CustomStepCategoryEnum.ReviseOrder,
  CustomStepCategoryEnum.ChangeVariant,
  CustomStepCategoryEnum.AdjustShippingFrequency,
  CustomStepCategoryEnum.AdjustTrialFrequency,
];

export const buttonLayoutSteps: StepCategory[] = [
  CustomStepCategoryEnum.AdjustShippingFrequency,
  CustomStepCategoryEnum.AdjustTrialFrequency,
  CustomStepCategoryEnum.PauseSurvey,
  CustomStepCategoryEnum.Survey,
  CustomStepCategoryEnum.CancelOrderSurvey,
  CustomStepCategoryEnum.CancelSubSurvey,
  CustomStepCategoryEnum.CancelTrialSurvey,
  CustomStepCategoryEnum.ReturnSurvey,
  CustomStepCategoryEnum.ResolveSurvey,
];
